import { z } from "zod";
import Modal from "../../../../../../components/Modal";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@apollo/client";
import FormInput from "../../../../../../components/FormInput";
import Button from "../../../../../../components/Button";
import {
  CreateEventDocument,
  DeleteEventDocument,
  EventDetailsDocument,
  EventFragment,
  OrganizationEventsDocument,
  ProjectDetailsDocument,
  UpdateEventDocument,
} from "../../../../../../__generated__/graphql";
import toast from "react-hot-toast";
// import { FaImage } from "react-icons/fa";
import { useEffect, useMemo, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import useSignedUrlToUploadFile from "../../../../../../hooks/useSignedUrlToUploadFile";
// import Cropper, { Area } from "react-easy-crop";
import { formatDay } from "../../../../../../lib/utils";
import useSignedUrlToGetFile from "../../../../../../hooks/useSignedUrlToGetFile";
import { TrashIcon } from "@heroicons/react/24/outline";
import LocationSearchPicker from "../../../../../../components/LocationSearchPicker";
import ProjectsSelection from "../../../../../../components/ProjectsSelection";

const eventSchema = z.object({
  eventName: z.string().min(1, "Un nom est requis"),
  description: z.string().optional(),
  startDate: z
    .string()
    .min(1, "La date est obligatoire")
    .transform((val) => new Date(val).toISOString()),
  locationLabel: z.string().optional(),
  locationAddress: z.string().optional(),
  locationZipCode: z.string().optional(),
  locationCity: z.string().optional(),
  locationCountryCode: z.string().optional(),
  locationLatitude: z.number().optional(),
  locationLongitude: z.number().optional(),
  locationPhotoUrls: z.array(z.string()).optional(),
  projectId: z.string().optional(),
});

type EventFormData = z.infer<typeof eventSchema>;

export default function EventModal({
  isOpen,
  onClose,
  projectId,
  onNewEvent,
  defaultEvent,
  defaultStartDate,
}: {
  isOpen: boolean;
  onClose: () => void;
  projectId?: string;
  onNewEvent?: (event: EventFragment) => void;
  defaultEvent?: EventFragment | null;
  defaultStartDate?: string; // Format: YYYY-MM-DD
}) {
  const isUpdate = Boolean(defaultEvent);
  // const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [eventImageFile, setEventImageFile] = useState<File | null>(null);
  // const [isCropping, setIsCropping] = useState(false);
  const [croppedImage, setCroppedImage] = useState<string | null>(null);
  // const [crop, setCrop] = useState({ x: 0, y: 0 });
  // const [zoom, setZoom] = useState(1);
  const [imageHasBeenUpdated, setImageHasBeenUpdated] = useState(false);

  const generatedBucketKey = useMemo(() => {
    return uuidv4();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const [createEvent, { loading: isCreatingEvent }] =
    useMutation(CreateEventDocument);
  const [updateEvent, { loading: isUpdatingEvent }] =
    useMutation(UpdateEventDocument);
  const [deleteEvent, { loading: isDeletingEvent }] =
    useMutation(DeleteEventDocument);
  const { data: signedUrlDataToUpload } =
    useSignedUrlToUploadFile(generatedBucketKey);
  const { data: signedUrlDataToGetFile } = useSignedUrlToGetFile(
    defaultEvent?.imageBucketKey
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
    setValue,
  } = useForm<EventFormData>({
    resolver: zodResolver(eventSchema),
  });

  useEffect(() => {
    if (signedUrlDataToGetFile?.signedUrlToGetFile) {
      setCroppedImage(signedUrlDataToGetFile.signedUrlToGetFile);
    }
  }, [signedUrlDataToGetFile, isOpen]);

  useEffect(() => {
    if (defaultEvent) {
      reset({
        projectId: defaultEvent.projectId || projectId,
        eventName: defaultEvent.name,
        description: defaultEvent.description || "",
        startDate: formatDay(defaultEvent.startDate),
        locationLabel: defaultEvent.eventLocation?.location.label,
        locationAddress: defaultEvent.eventLocation?.location.address,
        locationZipCode: defaultEvent.eventLocation?.location.zipCode,
        locationCity: defaultEvent.eventLocation?.location.city,
        locationCountryCode: defaultEvent.eventLocation?.location.countryCode,
        locationPhotoUrls: defaultEvent.eventLocation?.location.photoUrls || [],
      });
    } else if (defaultStartDate) {
      reset({
        startDate: defaultStartDate,
      });
    }
  }, [defaultEvent, projectId, reset, defaultStartDate]);

  const handleClose = () => {
    setImageHasBeenUpdated(false);
    reset();
    setEventImageFile(null);
    setCroppedImage(null);
    onClose();
  };

  const handleDelete = async () => {
    const response = confirm(
      "Voulez-vous vraiment supprimer cet évènement ? Cette action supprimera les activités, les participants et les documents associés"
    );

    if (!response) return;

    try {
      await deleteEvent({
        variables: {
          eventId: defaultEvent?.id || "",
        },
      });

      window.location.reload();
    } catch (error) {
      console.error(error);
      toast.error(
        "Une erreur est survenue lors de la suppression de l'évènement"
      );
    }
  };

  const onSubmit = async (data: EventFormData) => {
    try {
      if (eventImageFile && imageHasBeenUpdated) {
        if (!signedUrlDataToUpload?.signedUrlToUploadFile) {
          return;
        }
        const response = await fetch(
          signedUrlDataToUpload.signedUrlToUploadFile,
          {
            method: "PUT",
            body: eventImageFile,
          }
        );

        if (!response.ok) {
          throw new Error();
        }
      }

      if (!isUpdate) {
        const { data: newEvent } = await createEvent({
          variables: {
            createEventInput: {
              projectId: data.projectId || projectId,
              name: data.eventName,
              description: data.description,
              startDate: data.startDate,
              locationLabel: data.locationLabel,
              locationAddress: data.locationAddress,
              locationZipCode: data.locationZipCode,
              locationCity: data.locationCity,
              locationCountryCode: data.locationCountryCode,
              locationLatitude: data.locationLatitude,
              locationLongitude: data.locationLongitude,
              locationPhotoUrls: data.locationPhotoUrls,
              imageBucketKey:
                eventImageFile && imageHasBeenUpdated
                  ? generatedBucketKey
                  : defaultEvent?.imageBucketKey,
            },
          },
          refetchQueries: [
            OrganizationEventsDocument,
            ...(projectId
              ? [
                  {
                    query: ProjectDetailsDocument,
                    variables: {
                      projectId,
                    },
                  },
                ]
              : []),
          ],
        });
        toast.success("Évènement créé avec succès");

        if (newEvent?.createEvent) {
          onNewEvent?.(newEvent.createEvent);
        }
      } else {
        if (!defaultEvent) {
          // This should never happen is isUpdate is true
          return;
        }

        const imageToUpload = imageHasBeenUpdated
          ? generatedBucketKey
          : defaultEvent?.imageBucketKey;

        await updateEvent({
          variables: {
            updateEventInput: {
              id: defaultEvent.id,
              name: data.eventName,
              projectId: data.projectId,
              description: data.description,
              startDate: data.startDate,
              locationLabel: data.locationLabel,
              locationAddress: data.locationAddress,
              locationZipCode: data.locationZipCode,
              locationCity: data.locationCity,
              locationCountryCode: data.locationCountryCode,
              locationLatitude: data.locationLatitude,
              locationLongitude: data.locationLongitude,
              locationPhotoUrls: data.locationPhotoUrls,
              imageBucketKey: croppedImage ? imageToUpload : null,
            },
          },
          refetchQueries: [
            {
              query: EventDetailsDocument,
              variables: {
                eventId: defaultEvent.id,
              },
            },
          ],
        });

        toast.success("Évènement modifié avec succès");
      }

      handleClose();
    } catch (error) {
      console.error(error);
      toast.error("Une erreur est survenue lors de la création de l'évènement");
    }
  };

  const locationInputs = () => {
    const locationAddress = watch("locationAddress");
    const locationLabel = watch("locationLabel");

    if (!locationAddress && !locationLabel) {
      return (
        <div className="flex flex-col gap-2 min-w-md">
          <div className="text-md font-medium">Lieu</div>
          <LocationSearchPicker
            onLocationSelect={(location) => {
              setValue("locationLabel", location.name);
              setValue("locationAddress", location.address);
              setValue("locationZipCode", location.zipcode || "");
              setValue("locationCity", location.city || "");
              setValue("locationCountryCode", location.countryCode || "");
              setValue("locationLatitude", location.latitude);
              setValue("locationLongitude", location.longitude);
              setValue("locationPhotoUrls", location.photoUrls || []);
            }}
          />
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-2">
        <div className="flex justify-between items-center">
          <div className="text-md font-medium">Lieu</div>
          <button
            className="text-accent hover:text-accent/80 transition-all duration-300 cursor-pointer"
            onClick={() => {
              setValue("locationAddress", "");
              setValue("locationCity", "");
              setValue("locationLabel", "");
              setValue("locationZipCode", "");
              setValue("locationCountryCode", "");
              setValue("locationLatitude", undefined);
              setValue("locationLongitude", undefined);
              setValue("locationPhotoUrls", []);
            }}
          >
            <div className="flex items-center gap-2 text-sm">
              Rechercher avec Google Maps
            </div>
          </button>
        </div>
        <div>
          <div className="text-sm font-medium text-secondary">Nom du lieu</div>
          <div className="text-sm text-primary">{watch("locationLabel")}</div>
          <div className="text-sm font-medium text-secondary">Adresse</div>
          <div className="text-sm text-primary">{watch("locationAddress")}</div>
          <div className="text-sm font-medium text-secondary">Code postal</div>
          <div className="text-sm text-primary">{watch("locationZipCode")}</div>
          <div className="text-sm font-medium text-secondary">Ville</div>
          <div className="text-sm text-primary">{watch("locationCity")}</div>
        </div>
      </div>
    );
  };

  return (
    <Modal
      className="min-w-xl"
      isModalOpen={isOpen}
      onClose={handleClose}
      title={isUpdate ? "Modifier l'évènement" : "Créer un évènement"}
      dataTestId="event-modal"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {/* <div className="flex flex-col gap-4">
          {illustrationImageComponent()}
        </div> */}

        <FormInput
          label="Nom de l'évènement"
          error={errors.eventName}
          id="eventName"
          register={register}
          dataTestId="event-name-input"
        />

        <div>
          <label className="block text-sm font-medium mb-1">
            Description (optionnel)
          </label>
          <textarea
            {...register("description")}
            rows={2}
            className="w-full p-2 rounded-md bg-input focus:outline-none focus:ring-2 focus:ring-card border border-border"
          />
        </div>

        {!projectId && !defaultEvent?.projectId ? (
          <div className="flex flex-col gap-2">
            <label htmlFor="projectId" className="block text-sm font-medium">
              Choisir un projet
              <span className="text-sm text-tertiary"> (optionnel)</span>
            </label>
            <ProjectsSelection
              selectedProjectId={watch("projectId")}
              onProjectSelect={(project) => setValue("projectId", project?.id)}
              placeholder="Sélectionner un projet"
              error={errors.projectId?.message}
            />
          </div>
        ) : null}

        <FormInput
          label="Date"
          error={errors.startDate}
          id="startDate"
          type="date"
          register={register}
        />

        {locationInputs()}

        <div className="flex flex-row gap-2">
          {isUpdate ? (
            <Button
              type="button"
              variant="secondary"
              onClick={handleDelete}
              isLoading={isDeletingEvent}
              className="flex items-center gap-2 text-red-400 hover:text-red-400/80 w-full"
              dataTestId="event-delete-button"
            >
              <TrashIcon className="w-5 h-5 cursor-pointer flex-shrink-0" />
              Supprimer
            </Button>
          ) : null}

          <Button
            type="submit"
            className="w-full"
            isLoading={isCreatingEvent || isUpdatingEvent}
            dataTestId="event-submit-button"
          >
            {isUpdate ? "Modifier" : "Créer"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
